import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getAdminActiveUsersStats } from "../../../utils/Api";

interface DataPoint {
  label: string;
  value: number;
  key: string;
  fill?: string;
}

interface StatsResponse {
  total_users: number;
  data: DataPoint[];
}

const COLORS = ["#22c55e", "#84cc16", "#eab308", "#f97316", "#ef4444"];

const ActiveUsersChart: React.FC = () => {
  const [data, setData] = useState<DataPoint[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getAdminActiveUsersStats();
        const stats: StatsResponse = response.data;
        const dataWithColors: DataPoint[] = stats.data.map((item, index) => ({
          label: item.label,
          value: item.value,
          key: item.key,
          fill: COLORS[index % COLORS.length] ?? "#6b7280",
        }));
        setData(dataWithColors);
        setTotalUsers(stats.total_users);
      } catch (err) {
        setError("Failed to load active user statistics");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 h-full">
      <div className="mb-5">
        <h2 className="text-sm font-semibold tracking-tight text-gray-900">Active users</h2>
        <p className="text-xs text-gray-400 mt-0.5">
          Total users: {totalUsers.toLocaleString()}
        </p>
      </div>

      {loading ? (
        <div className="h-64 sm:h-72 flex items-center justify-center">
          <p className="text-sm text-gray-400">Loading...</p>
        </div>
      ) : error ? (
        <div className="h-64 sm:h-72 flex items-center justify-center">
          <p className="text-sm text-red-500">{error}</p>
        </div>
      ) : data.length === 0 ? (
        <div className="h-64 sm:h-72 flex items-center justify-center">
          <p className="text-sm text-gray-400">No data available</p>
        </div>
      ) : (
        <div className="h-64 sm:h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis
                type="number"
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false}
                axisLine={{ stroke: "#e5e7eb" }}
                allowDecimals={false}
              />
              <YAxis
                type="category"
                dataKey="label"
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false}
                axisLine={false}
                width={100}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  fontSize: "12px",
                }}
                formatter={(value) => [
                  typeof value === "number" ? value.toLocaleString() : value,
                  "Users",
                ]}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default ActiveUsersChart;
